/**
 * RD_SSI_Autism.js — Autism Semi-Structured Interview Review Document
 *
 * This is the clinician-facing paragraph review page for the Autism SSI module.
 * Clinicians step through each LLM-generated paragraph, approve, edit, reset,
 * or skip each one, then complete the review to mark the SSI as approved.
 *
 * ─── KEY ARCHITECTURAL DIFFERENCES FROM ADHD RD_SSI.js ───────────────────────
 *
 * 1. DATA SOURCE: Reads from the modular subcollection architecture via
 *    ModularPatientDataContext (reportContent.semiStructuredInterview), not the
 *    monolithic patientDataModel.dataModel['6_manageReports'].sections['2'].
 *
 * 2. APPROVAL TRACKING: Uses useModularApprovalTracking which accepts outputs and
 *    approvedParagraphs directly (no module number / patient doc reads needed).
 *
 * 3. FIRESTORE WRITES: All paragraph-level writes (approvals, edits, resets) go
 *    to the subcollection doc patients/{uid}/adult_autism/assessment_001/
 *    reportContent/semiStructuredInterview via targeted per-key updateDoc calls
 *    (no read-modify-write transaction, to avoid document-version contention on
 *    slow networks) on a doc ref built from getPatientReportContentPathSegments.
 *
 * 4. DOCUMENT RENDERING: On a fully-approved exit where something changed this
 *    session, we fire TriggerRenderAutismDocTemplate (stageKey
 *    'semiStructuredInterview') to (re)generate the SSI PDF/DOCX via the generic
 *    renderAndStoreAutismDocument Cloud Function. This mirrors the ADHD History
 *    gating; there is no per-paragraph re-render or cover-page logic.
 *
 * 5. NO SSI COMPLETION EMAIL: Autism SSI does not trigger the SSI email workflow.
 *
 * 6. NO FEATURE FLAG / VERSION CHECK: Autism is v2-only from inception — no legacy
 *    v1 paragraph structure, no reportLogicVersion switching.
 *
 * 7. NO STRENGTHS SECTION: Autism SSI doesn't have ADHD-style strength paragraphs,
 *    so no delete action, no STRENGTH_CODES set, no delete confirmation dialog.
 *
 * 8. NAVIGATION: On exit, navigates to the Autism documents dashboard at
 *    /patient/${patientId}/adult-autism/documents, not /patient/${patientId}/documents.
 *
 * ─── DOCUMENT STRUCTURE ──────────────────────────────────────────────────────
 *
 * The Autism SSI report is composed of the DSM-5 Criteria: 7 criterion
 * paragraphs (A1-A3 social communication, B1-B4 restricted/repetitive
 * behaviours). Functional impairment is captured within the criteria pages
 * themselves, so there are no separate functional-impairment paragraphs.
 *
 * Each section maps a single paragraph key "1" to a code like "asd_ssi_A1".
 * Unlike ADHD, there are no separate Adulthood/Childhood sub-paragraphs.
 */

import React, { useState, useMemo, useEffect, useRef, useCallback, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { ModularPatientDataContext } from '../../../context/ModularPatientDataContext';
import { useModularApprovalTracking } from '../../../hooks/useModularApprovalTracking';
import { usePendingApprovals } from '../../../hooks/usePendingApprovals';
import { finalizeModularSectionReview } from '../../../utils/sectionReviewFinalization';
import { computeUnpersistedApprovals } from '../../../utils/approvalStateUtils';
import { toast } from 'react-toastify';
import { useFloatingActionPanel } from './components/floatingPanel/useFloatingActionPanel';
import { FloatingActionPanel } from './components/floatingPanel/FloatingActionPanel';
import { InitialWarningDialog } from './components/InitialWarningDialog';
import WarningBar from './components/WarningBar';
import DocumentReviewHeader from './components/DocumentReviewHeader';
import { decodeHtmlEntities } from './functions/htmlEncoders';
import { handlePlainTextPaste, suppressFormattingShortcuts } from './functions/contentEditableUtils';
import { renderModelInput } from '../../../utils/renderModelInput';
import { useAuth } from '../../../hooks/useAuth';
import { useCanEditStage } from '../../../hooks/useCanEditStage';
import { STAGE_TO_CLINICIAN_KEY } from '../../../constants/adultAutismStages';
import { appendReportAuditEntry } from '../../../utils/reportAuditHelpers';
import { TriggerRenderAutismDocTemplate } from '../../../utils/TriggerRenderAutismDocTemplate';
import { rerenderAutismPatientReportIfStale } from '../../../utils/autoAutismPatientReport';
import { db, auth } from '../../../firebase';
import { doc as firestoreDoc, updateDoc, deleteField, serverTimestamp } from 'firebase/firestore';
import { getPatientReportContentPathSegments } from '../../../constants/adultAutismFirestorePaths';
import './styles/ReviewDocuments.css';
import {
  emitParagraphApproval,
  emitParagraphEdited,
  emitParagraphReset,
} from '../../../analytics/reportEvents';

// ─── SECTION CONSTANTS ────────────────────────────────────────────────────────
// Exported separately so they can be tested independently of the React component.
// Each section key is the heading displayed to the clinician; the inner map has
// a single entry "1" because Autism SSI uses one unified paragraph per criterion
// (no Adulthood/Childhood split like ADHD).

/**
 * DSM-5 Criterion paragraphs for the Autism SSI.
 * Domain A (social communication deficits): 3 items (A1-A3)
 * Domain B (restricted/repetitive behaviours): 4 items (B1-B4)
 */
export const ASD_SSI_CRITERION_SECTIONS = {
  "Criterion A1: Social-Emotional Reciprocity": {
    "1": { title: "Social-Emotional Reciprocity", code: "asd_ssi_A1" },
  },
  "Criterion A2: Nonverbal Communicative Behaviours": {
    "1": { title: "Nonverbal Communicative Behaviours", code: "asd_ssi_A2" },
  },
  "Criterion A3: Developing, Maintaining, and Understanding Relationships": {
    "1": { title: "Relationships", code: "asd_ssi_A3" },
  },
  "Criterion B1: Stereotyped or Repetitive Motor Movements, Use of Objects, or Speech": {
    "1": { title: "Stereotyped/Repetitive Behaviours", code: "asd_ssi_B1" },
  },
  "Criterion B2: Insistence on Sameness, Inflexible Adherence to Routines": {
    "1": { title: "Insistence on Sameness", code: "asd_ssi_B2" },
  },
  "Criterion B3: Highly Restricted, Fixated Interests": {
    "1": { title: "Restricted Interests", code: "asd_ssi_B3" },
  },
  "Criterion B4: Hyper- or Hyporeactivity to Sensory Input": {
    "1": { title: "Sensory Reactivity", code: "asd_ssi_B4" },
  },
};

// Functional Impairment is no longer a standalone SSI report section: impairment
// is now captured within the criteria pages themselves, so the Autism SSI report
// is composed solely of the DSM-5 criterion paragraphs above.

// ─── COMPONENT ────────────────────────────────────────────────────────────────

export default function ReviewDocumentSSI_Autism() {
  const { patientId } = useParams();
  const { claims } = useAuth();
  const navigate = useNavigate();

  // Human-readable identifiers for this review module. MODULE_PREFIX_CODE is
  // used as the Firestore field prefix for approval flags on the reportContent doc.
  const MODULE_TITLE = 'Semi-Structured Interview';
  const MODULE_PREFIX_CODE = "ssi";

  // ─── Context: subcollection-based data ──────────────────────────────────────
  // ModularPatientDataContext exposes reportContent keyed by stageKey. The SSI
  // data lives at reportContent.semiStructuredInterview. We also use fetchPatientData
  // to force a fresh load on mount (same pattern as ADHD RD pages).
  const {
    header,
    reportContent,
    updateReportContent,
    refreshReportContent,
    clearPatientData,
    fetchPatientData,
    loadedPatientId,
  } = useContext(ModularPatientDataContext);

  useEffect(() => {
    fetchPatientData(patientId);
  }, [patientId, fetchPatientData]);

  // ─── Edit permission gate ───────────────────────────────────────────────────
  // Only the clinician assigned as primary for the SSI module may review, edit,
  // and approve these AI paragraphs. Every other provider who can open the
  // Documents Dashboard lands here read-only. Defence in depth behind the
  // dashboard's assignee-gated Review button so a deep link cannot become an
  // editing back door. Reads the same `header.clinicians.clinicianSSI` slot the
  // dashboard uses (respecting any active SSI Lead override, per
  // isClinicianAssignedToStage).
  const canEdit = useCanEditStage(header, 'SSI');
  const assignedClinicianName =
    header?.clinicians?.[STAGE_TO_CLINICIAN_KEY.semiStructuredInterview]?.clinicianName;

  // ─── Derived data from the SSI reportContent document ───────────────────────
  // The reportContent doc for semiStructuredInterview has:
  //   outputs: { code -> paragraphText }
  //   inputs:  { code -> { "Clinician Notes": ..., "Clinician Decisions": ..., ... } }
  //   approvedParagraphs: { code -> { approvedAt, approvedBy, ... } }
  //   editHistory: { code -> { timestampKey -> entry } }
  const ssiData = reportContent?.semiStructuredInterview || {};
  const REPORT_DATA_OUTPUTS = useMemo(() => ssiData.outputs || {}, [ssiData.outputs]);

  // The paragraph edit history, read from the SAME already-loaded section as the
  // outputs above. It is what lets the adoption numbers (how much of the AI draft
  // survived) be computed at the moment of an edit rather than by reading the
  // report back out of Firestore later - and because this object is already in
  // memory to render the page, it costs no additional read.
  //
  // In-memory means it can lag a write made elsewhere in the same instant. That
  // only matters for a paragraph's FIRST edit, and only if another session edited
  // it seconds earlier; once the earliest entry exists it stays the earliest
  // forever. The nightly reconciliation reads authoritative Firestore, so a
  // disagreement surfaces there rather than going unnoticed.
  const REPORT_EDIT_HISTORY = useMemo(() =>
    ssiData.editHistory || {},
    [ssiData.editHistory]);
  const REPORT_DATA_INPUTS = useMemo(() => ssiData.inputs || {}, [ssiData.inputs]);
  const REPORT_DATA_APPROVED = useMemo(() => ssiData.approvedParagraphs || {}, [ssiData.approvedParagraphs]);

  // ─── Document structure ─────────────────────────────────────────────────────
  // Autism is v2-only: a single, static structure of the DSM-5 criterion
  // sections. No feature flag, no legacy structure. Functional Impairment is
  // captured within the criteria pages, so it has no separate paragraphs here.
  const docStructure = useMemo(() => ({
    ...ASD_SSI_CRITERION_SECTIONS,
  }), []);

  // ─── UI state ───────────────────────────────────────────────────────────────
  const [state, setState] = useState({
    selectedCode: null,
    textareaContent: null,
    isSidePanelOpen: false,
    clinicianInputs: null,
    clinicianDecisions: null,
    checkedExamples: null,
    patientInputs: null,
    informantInputs: null,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [showInitialWarning, setShowInitialWarning] = useState(true);
  const [initialContent, setInitialContent] = useState({});
  const [preEditContent, setPreEditContent] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [sectionDirty, setSectionDirty] = useState(false);

  // ─── Approval tracking via modular hook ─────────────────────────────────────
  // Unlike ADHD's useApprovalTracking (which reads from a patient doc snapshot
  // listener), useModularApprovalTracking accepts the data directly. It computes
  // totalParagraphs from docStructure + outputs and seeds reviewedCodes from
  // approvedParagraphs intersected with valid codes.
  const {
    totalParagraphs,
    reviewedCodes,
    isFullyApproved,
    showWarning,
    addReviewedCode,
    removeReviewedCode,
  } = useModularApprovalTracking({
    docStructure,
    reportDataOutputs: REPORT_DATA_OUTPUTS,
    reportDataApproved: REPORT_DATA_APPROVED,
  });

  // Tracks in-flight per-paragraph approval writes so Save & Exit can wait for
  // them to commit before deriving and persisting the section approval flag.
  const { track: trackApproval, settleAll: settlePendingApprovals } = usePendingApprovals();

  // ─── Snapshot initial content on first load ─────────────────────────────────
  // Captures the original AI-generated paragraph text so the "Reset" action
  // can revert edits to the as-generated state.
  useEffect(() => {
    if (REPORT_DATA_OUTPUTS && Object.keys(REPORT_DATA_OUTPUTS).length > 0) {
      const initialValues = {};
      Object.values(docStructure).forEach(section => {
        Object.values(section).forEach(({ code }) => {
          if (code && REPORT_DATA_OUTPUTS[code] !== undefined && REPORT_DATA_OUTPUTS[code] !== "") {
            initialValues[code] = REPORT_DATA_OUTPUTS[code];
          }
        });
      });
      setInitialContent(initialValues);
    }
  }, [REPORT_DATA_OUTPUTS, docStructure]);

  // ─── Refs and floating panel ────────────────────────────────────────────────
  const documentContainerRef = useRef(null);
  const editableContentRef = useRef(null);

  const { panelRef, panelPosition, safeUpdatePosition } = useFloatingActionPanel({
    containerRef: documentContainerRef,
    selectedCode: state.selectedCode,
    isEditing,
    topOffset: -15,
  });

  // ─── Firestore doc reference for the SSI reportContent document ─────────────
  // All transactional writes (approvals, edits, resets) target this single doc.
  const getDocRef = useCallback(() => {
    const reportContentPath = getPatientReportContentPathSegments(patientId, 'semiStructuredInterview');
    return firestoreDoc(db, ...reportContentPath);
  }, [patientId]);

  // ─── Paragraph click handler ────────────────────────────────────────────────
  // When the clinician clicks a paragraph, we select it, load its AI inputs into
  // the side panel, and position the floating action panel adjacent to it.
  const handleParagraphClick = (event, code, content) => {
    setIsEditing(false);

    if (!REPORT_DATA_INPUTS || !REPORT_DATA_INPUTS[code]) {
      console.error('No matching input found for the code:', code);
      return;
    }

    setState(prevState => ({
      ...prevState,
      selectedCode: code,
      textareaContent: content || ' ',
      clinicianInputs: REPORT_DATA_INPUTS[code]["Clinician Notes"],
      clinicianDecisions: REPORT_DATA_INPUTS[code]["Clinician Decisions"],
      checkedExamples: REPORT_DATA_INPUTS[code]["Checklist Examples Ticked"],
      patientInputs: REPORT_DATA_INPUTS[code]["Patient Prefill"],
      informantInputs: REPORT_DATA_INPUTS[code]["Informant Prefill"],
      isSidePanelOpen: true,
    }));

    setTimeout(() => {
      safeUpdatePosition(code);
    }, 0);
  };

  // ─── Section component ──────────────────────────────────────────────────────
  // Renders a group of paragraphs under a heading. Only shows sections that have
  // at least one paragraph with generated content (blank codes are hidden).
  const Section = ({ title, paragraphs }) => {
    const availableParagraphs = Object.entries(paragraphs).filter(
      ([_, { code }]) => REPORT_DATA_OUTPUTS[code] !== undefined && REPORT_DATA_OUTPUTS[code] !== ""
    );

    if (availableParagraphs.length === 0) return null;

    return (
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-1 text-blue-500">{title}</h2>
        <div className="mb-3" />

        {availableParagraphs.map(([key, { title: subTitle, code }]) => {
          const content = REPORT_DATA_OUTPUTS[code];
          const decodedContent = decodeHtmlEntities(content);
          return (
            <div key={key} className="mb-4">
              {subTitle && (
                <h3 className="font-semibold mb-2 text-gray-900">
                  {subTitle}
                </h3>
              )}
              <div
                data-paragraph-container
                className={`
                  relative text-sm font-medium p-2 rounded-lg
                  transition-all duration-200
                  ${isEditing && state.selectedCode === code
                    ? 'bg-amber-100 z-20'
                    : reviewedCodes.has(code)
                      ? 'bg-green-600/30 hover:bg-green-600/40 approved-paragraph'
                      : 'bg-violet-100 hover:bg-violet-200'
                  }
                  ${state.selectedCode === code ? 'ring ring-violet-800' : ''}
                  ${!isEditing ? 'cursor-pointer' : ''}
                `}
                id={code}
                onClick={(e) => {
                  if (!isEditing) {
                    handleParagraphClick(e, code, decodedContent);
                  }
                }}
              >
                <p
                  ref={editableContentRef}
                  className="text-gray-700 leading-relaxed whitespace-pre-wrap"
                  contentEditable={isEditing && state.selectedCode === code}
                  onKeyDown={suppressFormattingShortcuts}
                  onInput={(e) => {
                    if (isEditing && state.selectedCode === code) {
                      let newContent = e.target.innerText;
                      newContent = newContent.replace(/\n/g, '\n');
                      if (REPORT_DATA_OUTPUTS) {
                        REPORT_DATA_OUTPUTS[code] = newContent;
                      }
                      safeUpdatePosition(code);
                    }
                  }}
                  onPaste={handlePlainTextPaste}
                  suppressContentEditableWarning={true}
                >
                  {decodedContent}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // ─── Edit handlers ──────────────────────────────────────────────────────────

  /**
   * Enters edit mode for the currently selected paragraph. Stores the pre-edit
   * content so the clinician can cancel and revert without a Firestore read.
   */
  const handleStartEdit = () => {
    if (!canEdit) return;
    if (state.selectedCode) {
      setPreEditContent(prev => ({
        ...prev,
        [state.selectedCode]: REPORT_DATA_OUTPUTS[state.selectedCode] || ''
      }));
    }
    setIsEditing(true);
    setTimeout(() => {
      const paragraphElement = document.querySelector(`#${CSS.escape(state.selectedCode)} p`);
      if (paragraphElement) {
        paragraphElement.focus();
        const range = document.createRange();
        const selection = window.getSelection();
        range.selectNodeContents(paragraphElement);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      safeUpdatePosition(state.selectedCode);
    }, 0);
  };

  /**
   * Saves the edited paragraph content to Firestore. Updates the outputs field,
   * removes the paragraph from approvedParagraphs (requiring re-approval), and
   * logs an entry in editHistory for audit trail.
   */
  const handleSaveEdit = () => {
    if (!canEdit) {
      toast.error('Only the clinician assigned to the Semi-Structured Interview module can edit these paragraphs.');
      return;
    }
    if (!state.selectedCode) return;

    const spanCode = state.selectedCode;
    const newValue = REPORT_DATA_OUTPUTS[spanCode];
    const previousValue = initialContent[spanCode] || '';

    if (newValue !== previousValue) {
      removeReviewedCode(state.selectedCode);
      setIsEditing(false);
      safeUpdatePosition(state.selectedCode);

      const editEntry = {
        clinicianId: auth.currentUser.uid,
        clinicianName: auth.currentUser.displayName,
        timestamp: serverTimestamp(),
        previousValue,
        newValue,
        editType: "manual",
      };
      const timestampKey = Date.now().toString();

      // Targeted per-key updateDoc (root fields on this stage's reportContent
      // subcollection doc) instead of a read-modify-write runTransaction. The
      // transaction's document-version precondition contends under slow 3G/4G
      // and silently discards the edit; a plain updateDoc on distinct field
      // paths does not. See handleParagraphApproval for the full rationale.
      const docRef = getDocRef();

      const startedAt = performance.now();
      updateDoc(docRef, {
        // Persist the edited text
        [`outputs.${spanCode}`]: newValue,
        // Editing invalidates any prior approval
        [`approvedParagraphs.${spanCode}`]: deleteField(),
        // Append an audit entry under this paragraph's edit history
        [`editHistory.${spanCode}.${timestampKey}`]: editEntry,
        // Review metadata (mirrors the approval write)
        lastReviewedAt: serverTimestamp(),
        reviewedBy: auth.currentUser.uid,
        reviewedByName: auth.currentUser.displayName,
      }).then(() => {
        // Confirms the edit persisted AND the paragraph was un-approved
        // (removed from approvedParagraphs) in Firestore. Code-only, no PII.
        const elapsedMs = Math.round(performance.now() - startedAt);
        console.log(`[edit] saved & UNAPPROVED "${spanCode}" (content updated) in ${elapsedMs}ms`);
        // Analytics mirror of the edit that just persisted: paragraph code,
        // section and the two lengths only — never the wording itself.
        emitParagraphEdited({
          patientId,
          module: MODULE_PREFIX_CODE,
          docStructure,
          code: spanCode,
          prevText: previousValue,
          priorHistory: REPORT_EDIT_HISTORY[spanCode] || {},
          nextText: newValue,
        });
      }).catch(error => {
        console.error("Error updating paragraph approval on edit:", error);
        // Roll back the optimistic un-approval and surface the failure so
        // the clinician does not believe their edit was saved.
        addReviewedCode(state.selectedCode);
        toast.error(
          'Your edit did not save. Please try editing the paragraph again.',
          { toastId: 'paragraph-edit-save-error' }
        );
      });

      setSectionDirty(true);
    } else {
      setIsEditing(false);
      safeUpdatePosition(state.selectedCode);
    }
  };

  /**
   * Cancels the current edit and reverts the paragraph text to what it was
   * before the clinician started editing (pre-edit content snapshot).
   */
  const handleCancel = () => {
    if (state.selectedCode && preEditContent[state.selectedCode]) {
      if (REPORT_DATA_OUTPUTS) {
        REPORT_DATA_OUTPUTS[state.selectedCode] = preEditContent[state.selectedCode];
      }
    }
    setIsEditing(false);
    safeUpdatePosition(state.selectedCode);
  };

  /**
   * Resets the paragraph back to the original AI-generated content (captured
   * on page load). Writes the reset to Firestore, removes the approval, and
   * logs an editHistory entry flagged as isReset for audit differentiation.
   */
  const handleReset = () => {
    if (!canEdit) {
      toast.error('Only the clinician assigned to the Semi-Structured Interview module can reset these paragraphs.');
      return;
    }
    if (!state.selectedCode || !initialContent[state.selectedCode]) return;

    const spanCode = state.selectedCode;
    const newValue = initialContent[spanCode];
    const previousValue = REPORT_DATA_OUTPUTS[spanCode];

    // Optimistic UI update
    if (REPORT_DATA_OUTPUTS) {
      REPORT_DATA_OUTPUTS[spanCode] = newValue;
    }
    removeReviewedCode(spanCode);

    const editEntry = {
      clinicianId: auth.currentUser.uid,
      clinicianName: auth.currentUser.displayName,
      timestamp: serverTimestamp(),
      previousValue,
      newValue,
      isReset: true,
    };
    const timestampKey = Date.now().toString();

    // Targeted per-key updateDoc (root fields) instead of a read-modify-write
    // runTransaction, which contends on the doc version under slow 3G/4G and
    // loses the reset. See handleSaveEdit / handleParagraphApproval.
    const docRef = getDocRef();

    const startedAt = performance.now();
    updateDoc(docRef, {
      // Restore the original generated text
      [`outputs.${spanCode}`]: newValue,
      // A reset also invalidates any prior approval
      [`approvedParagraphs.${spanCode}`]: deleteField(),
      // Record the reset in this paragraph's edit history
      [`editHistory.${spanCode}.${timestampKey}`]: editEntry,
      // Review metadata (mirrors the approval write)
      lastReviewedAt: serverTimestamp(),
      reviewedBy: auth.currentUser.uid,
      reviewedByName: auth.currentUser.displayName,
    }).then(() => {
      // Confirms the reset persisted AND the paragraph was un-approved
      // (removed from approvedParagraphs) in Firestore. Code-only, no PII.
      const elapsedMs = Math.round(performance.now() - startedAt);
      console.log(`[reset] reset & UNAPPROVED "${spanCode}" in ${elapsedMs}ms`);
      // Analytics mirror of the reset that just persisted (code only).
      emitParagraphReset({
        patientId,
        module: MODULE_PREFIX_CODE,
        docStructure,
        code: spanCode,
      });
    }).catch(error => {
      console.error("Error resetting paragraph:", error);
      // Roll back both content and approval, then surface the failure so the
      // clinician knows the reset did not persist.
      if (REPORT_DATA_OUTPUTS) {
        REPORT_DATA_OUTPUTS[spanCode] = previousValue;
      }
      addReviewedCode(spanCode);
      toast.error(
        'The paragraph reset did not save. Please try again.',
        { toastId: 'paragraph-reset-save-error' }
      );
    });

    setIsEditing(false);
    safeUpdatePosition(spanCode);
    setSectionDirty(true);
  };

  /**
   * Checks if a paragraph's current content differs from the initial AI output.
   * Used to conditionally show the "Reset" button in the floating panel.
   */
  const hasBeenModified = (code) => {
    if (!code) return false;
    return REPORT_DATA_OUTPUTS[code] !== initialContent[code];
  };

  // ─── Paragraph approval ─────────────────────────────────────────────────────
  // Writes or removes the approval record for a single paragraph on the
  // reportContent subcollection document.
  const handleParagraphApproval = async (code, isApproving) => {
    if (!canEdit) {
      toast.error('Only the clinician assigned to the Semi-Structured Interview module can approve these paragraphs.');
      return;
    }
    const docRef = getDocRef();

    try {
      // Targeted per-key write instead of a full read-modify-write transaction
      // on approvedParagraphs — avoids document-version contention when a
      // clinician approves many paragraphs quickly on slow 3G/4G links (see
      // RD_CI.js for the full rationale). `code` is a dot-free identifier and
      // the fields below are siblings, so there is no conflicting field-path
      // rejection.
      const updateData = {
        [`approvedParagraphs.${code}`]: isApproving
          ? {
              approvedAt: serverTimestamp(),
              approvedBy: auth.currentUser.uid,
              approvedByName: auth.currentUser.displayName,
              content: REPORT_DATA_OUTPUTS[code],
            }
          : deleteField(),
        lastReviewedAt: serverTimestamp(),
        reviewedBy: auth.currentUser.uid,
        reviewedByName: auth.currentUser.displayName,
      };

      // Timed, PII-free confirmation log (updateDoc resolves on server ack).
      const startedAt = performance.now();
      await updateDoc(docRef, updateData);
      const elapsedMs = Math.round(performance.now() - startedAt);
      console.log(
        `[approval] ${isApproving ? 'APPROVED' : 'UNAPPROVED'} "${code}" persisted to Firestore in ${elapsedMs}ms`
      );

      // Analytics mirror of the approval change that just persisted
      // (code only). Both directions share the review control that
      // wrote it, so both share this one call.
      emitParagraphApproval({
        patientId,
        module: MODULE_PREFIX_CODE,
        docStructure,
        code,
        assessmentType: 'autism',
        isApproving,
      });

      setSectionDirty(true);
    } catch (error) {
      console.error("Error updating paragraph approval:", error);
      // Re-throw so the caller can observe the failure. Swallowing the error
      // here made failed approval writes invisible: the paragraph stayed green
      // on screen with no persisted approval behind it — the proven cause of
      // reports finalising with missing / unapproved content.
      throw error;
    }
  };

  // ─── Mark as Done (approve + advance) ──────────────────────────────────────
  // Approves the currently selected paragraph, then scrolls to and selects the
  // next un-reviewed paragraph. If no un-reviewed paragraphs remain, deselects.
  const handleMarkAsDone = async () => {
    if (!canEdit) {
      toast.error('Only the clinician assigned to the Semi-Structured Interview module can approve these paragraphs.');
      return;
    }
    if (!state.selectedCode) return;

    const paragraphElement = document.getElementById(state.selectedCode);
    if (!paragraphElement) return;

    try {
      // Track the in-flight write so Save & Exit awaits it before finalising.
      trackApproval(
        handleParagraphApproval(state.selectedCode, true).catch(error => {
          console.error('Error in handleParagraphApproval:', error);
          // Roll back the optimistic update and tell the clinician — a
          // silently failed approval is exactly how incomplete sections
          // used to slip through to the rendered report.
          removeReviewedCode(state.selectedCode);
          toast.error(
            'A paragraph approval did not save and has been returned to "needs review". Please approve it again.',
            { toastId: 'paragraph-approval-save-error' }
          );
        })
      );

      const allParagraphs = document.querySelectorAll('[data-paragraph-container]');
      const currentIndex = Array.from(allParagraphs).findIndex(p => p.id === state.selectedCode);
      const nextUnreviewed = Array.from(allParagraphs).find((p, index) =>
        index > currentIndex && !reviewedCodes.has(p.id)
      );

      addReviewedCode(state.selectedCode);

      if (nextUnreviewed && documentContainerRef.current) {
        const containerRect = documentContainerRef.current.getBoundingClientRect();
        const nextRect = nextUnreviewed.getBoundingClientRect();
        const scrollTop = documentContainerRef.current.scrollTop +
          (nextRect.top - containerRect.top) -
          containerRect.height / 3;

        documentContainerRef.current.scrollTo({
          top: scrollTop,
          behavior: 'smooth',
        });

        setTimeout(() => {
          const nextCode = nextUnreviewed.id;
          setState(prev => ({
            ...prev,
            selectedCode: nextCode,
            isSidePanelOpen: true,
            clinicianInputs: REPORT_DATA_INPUTS[nextCode]?.["Clinician Notes"],
            clinicianDecisions: REPORT_DATA_INPUTS[nextCode]?.["Clinician Decisions"],
            checkedExamples: REPORT_DATA_INPUTS[nextCode]?.["Checklist Examples Ticked"],
            patientInputs: REPORT_DATA_INPUTS[nextCode]?.["Patient Prefill"],
            informantInputs: REPORT_DATA_INPUTS[nextCode]?.["Informant Prefill"],
          }));
          setTimeout(() => { safeUpdatePosition(nextCode); }, 0);
        }, 300);
      } else {
        setState(prev => ({
          ...prev,
          selectedCode: null,
          isSidePanelOpen: false,
          clinicianInputs: null,
          clinicianDecisions: null,
          checkedExamples: null,
          patientInputs: null,
          informantInputs: null,
        }));
      }
    } catch (error) {
      console.error('Error in handleMarkAsDone:', error);
    }
  };

  // ─── Skip (advance without approving) ──────────────────────────────────────
  // Moves to the next un-reviewed paragraph without marking the current one
  // as approved. Useful when the clinician wants to come back later.
  const handleSkip = () => {
    if (!canEdit) return;
    if (!state.selectedCode) return;

    const allParagraphs = document.querySelectorAll('[data-paragraph-container]');
    const currentIndex = Array.from(allParagraphs).findIndex(p => p.id === state.selectedCode);
    const nextUnreviewed = Array.from(allParagraphs).find((p, index) =>
      index > currentIndex && !reviewedCodes.has(p.id)
    );

    if (nextUnreviewed && documentContainerRef.current) {
      const containerRect = documentContainerRef.current.getBoundingClientRect();
      const nextRect = nextUnreviewed.getBoundingClientRect();
      const scrollTop = documentContainerRef.current.scrollTop +
        (nextRect.top - containerRect.top) -
        containerRect.height / 3;

      documentContainerRef.current.scrollTo({
        top: scrollTop,
        behavior: 'smooth',
      });

      setTimeout(() => {
        const nextCode = nextUnreviewed.id;
        setState(prev => ({
          ...prev,
          selectedCode: nextCode,
          isSidePanelOpen: true,
          clinicianInputs: REPORT_DATA_INPUTS[nextCode]?.["Clinician Notes"],
          clinicianDecisions: REPORT_DATA_INPUTS[nextCode]?.["Clinician Decisions"],
          checkedExamples: REPORT_DATA_INPUTS[nextCode]?.["Checklist Examples Ticked"],
          patientInputs: REPORT_DATA_INPUTS[nextCode]?.["Patient Prefill"],
          informantInputs: REPORT_DATA_INPUTS[nextCode]?.["Informant Prefill"],
        }));
        setTimeout(() => { safeUpdatePosition(nextCode); }, 0);
      }, 300);
    } else {
      setState(prev => ({
        ...prev,
        selectedCode: null,
        isSidePanelOpen: false,
        clinicianInputs: null,
        clinicianDecisions: null,
        checkedExamples: null,
        patientInputs: null,
        informantInputs: null,
      }));
    }
  };

  // ─── Go to first un-reviewed ───────────────────────────────────────────────
  // Scrolls the document to the first paragraph that hasn't been approved yet.
  // Triggered by the header's "Next" button.
  const goToFirstUnreviewed = () => {
    const allParagraphs = document.querySelectorAll('[data-paragraph-container]');
    const firstUnreviewed = Array.from(allParagraphs).find(p => !reviewedCodes.has(p.id));

    if (firstUnreviewed && documentContainerRef.current) {
      const containerRect = documentContainerRef.current.getBoundingClientRect();
      const nextRect = firstUnreviewed.getBoundingClientRect();
      const scrollTop = documentContainerRef.current.scrollTop +
        (nextRect.top - containerRect.top) -
        containerRect.height / 3;

      documentContainerRef.current.scrollTo({
        top: scrollTop,
        behavior: 'smooth',
      });

      setTimeout(() => {
        const nextCode = firstUnreviewed.id;
        const nextContent = REPORT_DATA_OUTPUTS[nextCode];
        handleParagraphClick(
          { currentTarget: firstUnreviewed },
          nextCode,
          nextContent
        );
      }, 300);
    }
  };

  // ─── Exit review ───────────────────────────────────────────────────────────
  // Writes the overall approval status to the reportContent doc and navigates
  // to the Autism documents dashboard. Unlike ADHD, no document rendering or
  // completion email is triggered.
  const handleExitReview = async () => {
    // Read-only viewers (not the SSI primary) can leave the page, but must
    // never run the finalise path: no approval/status writes, no audit entry,
    // and no PDF/patient-report render. Just drop cached data and navigate back
    // to the dashboard.
    if (!canEdit) {
      // Never act on assignment until the patient doc has loaded for THIS
      // patient. canEdit starts false and only becomes true once `header`
      // hydrates, so a legitimate assignee who clicks before that would
      // otherwise be treated as read-only and navigated away without
      // finalising. Once loaded, canEdit is authoritative.
      if (loadedPatientId !== patientId) {
        toast.error(
          'The review is still loading. Please wait a moment and try again.',
          { toastId: 'review-exit-not-loaded' }
        );
        return;
      }
      clearPatientData();
      navigate(`/patient/${patientId}/adult-autism/documents`);
      return;
    }

    setIsSaving(true);

    try {
      // Guard: never finalise against a document that has not fully loaded for
      // THIS patient. If reportContent is empty/stale the approval denominator
      // is wrong, which could either write a false "approved" flag with
      // unapproved content behind it, or revert a previously-approved flag.
      if (loadedPatientId !== patientId) {
        toast.error(
          'The review is still loading. Please wait a moment and try again.',
          { toastId: 'review-exit-not-loaded' }
        );
        setIsSaving(false);
        return;
      }

      // Wait for any optimistic per-paragraph approval writes to commit so the
      // finalisation below reads the true, current approval state from Firestore.
      await settlePendingApprovals();

      // Derive and persist the section approval flag transactionally from the
      // fresh document snapshot. This replaces writing the client's optimistic
      // isFullyApproved, which could diverge from Firestore and finalise an
      // incomplete report. The first-approval stamp is likewise snapshot-derived.
      const { isFullyApproved: approvedNow, outstandingCodes, wasFirstApproval } =
        await finalizeModularSectionReview({
          patientId,
          stageKey: 'semiStructuredInterview',
          flagPrefix: MODULE_PREFIX_CODE,
          docStructure,
        });

      // Reconcile the optimistic UI against the authoritative snapshot before
      // allowing the exit. Stay on the page when phantom approvals exist (green
      // on screen but the background write failed) or when the snapshot found
      // outstanding content the UI believed was done.
      const unpersisted = computeUnpersistedApprovals(reviewedCodes, outstandingCodes);
      if (!approvedNow && (isFullyApproved || unpersisted.length > 0)) {
        unpersisted.forEach(removeReviewedCode);
        await refreshReportContent(patientId, 'semiStructuredInterview');
        toast.error(
          unpersisted.length > 0
            ? `${unpersisted.length} paragraph approval${unpersisted.length === 1 ? ' did' : 's did'} not save. `
              + 'The affected paragraphs have been returned to "needs review" — please approve them again.'
            : 'Some paragraphs still need review before this section can be completed. '
              + 'The page has been refreshed to show them.',
          { toastId: 'review-exit-unsaved' }
        );
        return;
      }

      // Fire-and-forget audit log entry. The first-occurrence flag comes from
      // the transaction's snapshot-derived result, not stale React context.
      appendReportAuditEntry(patientId, {
        module: MODULE_PREFIX_CODE,
        eventType: 'approved',
        isFirstOccurrence: wasFirstApproval,
      });

      // Kick off PDF/DOCX rendering of the SSI report. Only (re)render when the
      // section is fully approved (snapshot-derived) AND something changed this
      // session (sectionDirty). Note: the trigger takes the canonical stage key
      // ('semiStructuredInterview'), not the short approval prefix ('ssi').
      if (approvedNow && sectionDirty) {
        TriggerRenderAutismDocTemplate({
          patientId,
          stageKey: 'semiStructuredInterview',
          clinicianId: auth.currentUser.uid,
        }).catch((err) => console.error('Error triggering autism SSI render:', err));
      }

      // Auto-finalise the combined Patient Report. Called unconditionally (fire-and-forget):
      // the helper reads fresh Firestore state and only (re)renders when all five section
      // reports are approved AND the patient report is stale — mirroring the ADHD flow, so
      // there is no manual "Generate" button. Safe even on a no-op exit.
      rerenderAutismPatientReportIfStale({ patientId, clinicianId: auth.currentUser.uid })
        .catch((err) => console.error('Autism PR auto-finalise error:', err));

      clearPatientData();
      setSectionDirty(false);
      navigate(`/patient/${patientId}/adult-autism/documents`);
    } catch (error) {
      console.error('Error during document review completion:', error);
      // Surface the failure so the clinician knows the exit did not complete —
      // silently staying on the page looks like a frozen button.
      toast.error(
        'Could not save your review just now. Please check your connection and try again.',
        { toastId: 'review-exit-error' }
      );
    } finally {
      setIsSaving(false);
    }
  };

  // ─── Progress calculation ──────────────────────────────────────────────────
  const calculateProgress = useCallback(() => {
    if (totalParagraphs === 0) return 0;
    return Math.round((reviewedCodes.size / totalParagraphs) * 100);
  }, [reviewedCodes.size, totalParagraphs]);

  // ─── Side effects: body overflow lock ──────────────────────────────────────
  // Prevent the page body from scrolling while the review UI is active.
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // ─── Side effects: scroll during edit mode ─────────────────────────────────
  // Keeps the document container scrollable even when the dimming overlay is up.
  useEffect(() => {
    const currentContainer = documentContainerRef.current;
    if (isEditing && currentContainer) {
      const originalOverflow = currentContainer.style.overflow;
      currentContainer.style.overflow = 'auto';

      const handleWheel = (e) => { e.stopPropagation(); };
      currentContainer.addEventListener('wheel', handleWheel, { passive: true });

      return () => {
        currentContainer.removeEventListener('wheel', handleWheel);
        currentContainer.style.overflow = originalOverflow;
      };
    }
  }, [isEditing]);

  // ─── Side effects: deselect on background click ────────────────────────────
  // Clicking outside any paragraph or the action panel deselects the current
  // paragraph and closes the side panel.
  const handlePageClick = useCallback((event) => {
    if (isEditing) return;
    if (
      event.target.closest('[data-paragraph-container]') ||
      event.target.closest('[data-action-panel]')
    ) {
      return;
    }
    setIsEditing(false);
    setState(prev => ({
      ...prev,
      selectedCode: null,
      selectedContent: null,
      isSidePanelOpen: false,
    }));
  }, [isEditing]);

  useEffect(() => {
    const container = documentContainerRef.current;
    if (container) {
      container.addEventListener('click', handlePageClick);
    }
    return () => {
      if (container) {
        container.removeEventListener('click', handlePageClick);
      }
    };
  }, [handlePageClick]);

  // ─── RENDER ─────────────────────────────────────────────────────────────────

  return (
    <Layout activePage="dashboard" scrollable={false}>
      <InitialWarningDialog showInitialWarning={showInitialWarning} setShowInitialWarning={setShowInitialWarning} />
      <div className="h-full flex flex-col overflow-hidden">

        {/* Dimming overlay during edit mode — pointer-events: none so the
            underlying contentEditable paragraph remains interactive */}
        {isEditing && (
          <div
            className="fixed inset-0 bg-gray-900/20 z-10 transition-opacity duration-200"
            style={{ pointerEvents: 'none' }}
          />
        )}

        {/* Fixed header section with progress bar and action buttons */}
        <div className="flex-none pt-4">
          <DocumentReviewHeader
            title={`Reviewing: ${MODULE_TITLE}`}
            reviewedCodes={reviewedCodes}
            totalParagraphs={totalParagraphs}
            isSaving={isSaving}
            handleExitReview={handleExitReview}
            goToFirstUnreviewed={goToFirstUnreviewed}
            calculateProgress={calculateProgress}
          />
          <WarningBar show={showWarning} />
          {!canEdit && header && (
            <div className="mt-2 bg-gray-100 border border-gray-300 rounded-lg px-4 py-2 text-sm text-gray-700">
              Read-only view. Only{assignedClinicianName ? ` ${assignedClinicianName}, the clinician` : ' the clinician'} assigned to the Semi-Structured Interview module can review, edit, and approve these paragraphs. You can still preview and download the report from the Documents dashboard.
            </div>
          )}
        </div>

        {/* Scrollable document section */}
        <div className="flex-1 px-8 pb-8 min-h-0 relative">
          <div className="document-display-container h-full overflow-hidden bg-slate-800">
            <div className="flex h-full">
              {/* Left side — Document container with paragraphs */}
              <div
                ref={documentContainerRef}
                className={`
                  w-1/2 relative m-4 p-16 border shadow-md bg-white
                  ${isEditing ? 'overflow-hidden' : 'overflow-y-auto'}
                `}
              >
                <div className="space-y-8 text-left">
                  {Object.entries(docStructure).map(([title, paragraphs]) => (
                    <Section
                      key={title}
                      title={title}
                      paragraphs={paragraphs}
                    />
                  ))}
                </div>
                {/* Spacer so the last paragraph can be scrolled to the centre */}
                <div className="h-[calc(100vh-32rem)]" aria-hidden="true" />

                {/* Floating Action Panel — approve/edit/skip/reset (no delete for Autism SSI) */}
                <FloatingActionPanel
                  panelRef={panelRef}
                  position={panelPosition}
                  visible={canEdit && Boolean(state.selectedCode)}
                  isEditing={isEditing}
                  hasBeenModified={hasBeenModified(state.selectedCode)}
                  onStartEdit={handleStartEdit}
                  onSave={handleSaveEdit}
                  onCancel={handleCancel}
                  onReset={handleReset}
                  onApprove={handleMarkAsDone}
                  onSkip={handleSkip}
                  canDelete={false}
                  onDelete={() => {}}
                />
              </div>

              {/* Right side — AI Inputs side panel showing what was fed to the LLM
                  for the currently selected paragraph. Helps clinicians understand
                  the basis of the generated text when deciding to approve or edit. */}
              <div className={`
                w-2/5 p-4 flex flex-col
                ${!state.selectedCode ? 'hidden' : ''}
              `}>
                <div className={`
                  flex-1 text-sm bg-gray-100 rounded-xl overflow-y-auto
                  border shadow-sm
                  flex flex-col h-full
                `}>
                  {state.selectedCode && (
                    <div className="flex flex-col h-full p-6 overflow-hidden">
                      <div className="flex-1 flex flex-col min-h-0">
                        <div className="flex-none flex justify-between items-center">
                          <p className="text-xl font-bold text-gray-800 mb-2">AI Inputs</p>
                        </div>
                        <div className="overflow-y-auto">
                          {renderModelInput("Your decisions", state.clinicianDecisions)}
                          {renderModelInput("Your notes", state.clinicianInputs)}
                          {renderModelInput("Your checked examples", state.checkedExamples)}
                          {renderModelInput("Patient's report", state.patientInputs)}
                          {renderModelInput("Informant comments", state.informantInputs)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
